import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@welldev-group/rcl-button';
import SelectBox from '@welldev-group/rcl-select-box';
import Icon from '@welldev-group/rcl-icon';
import IconButton from '@welldev-group/rcl-icon-button';
import Modal from '@welldev-group/rcl-modal';
import { I18nText, Avatar } from '@welldev-group/react-comp-lib';

import selectBoxObjectMapper from '../../../../shared/helper/selectBoxObjectMapper';

const AffiliateAccountManager = ({
  accountManagerId,
  accountManagerName,
  accountManagerAvatar,
  accountManagerTitle,
  accountManagerOptions,
  hasAccountManager,
  updateAffiliate,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAccountManagerId, setSelectedAccountManagerId] = useState(
    accountManagerId,
  );

  const onModalClose = () => {
    setModalOpen(false);
  };

  const onModalOpen = () => {
    setModalOpen(true);
  };

  const onSelectedAccountManagerIdChange = value => {
    const params = {
      affiliate: {
        account_manager_id: value,
      },
    };
    return updateAffiliate(params);
  };

  const onSelectedAccountManagerIdAssign = value => {
    onSelectedAccountManagerIdChange(value).then(() => onModalClose());
  };

  const onSelectedAccountManagerIdRemove = value => {
    setSelectedAccountManagerId(value);
    onSelectedAccountManagerIdChange(value);
  };

  return (
    <div className="affiliate-account-manager__wrapper">
      <div className="affiliate-account-manager">
        {hasAccountManager ? (
          <div className="affiliate-account-manager__information">
            <div className="affiliate-account-manager__avatar">
              <Avatar
                src={accountManagerAvatar}
                firstName={accountManagerName}
                size="large"
              />
            </div>
            <div>
              <div className="affiliate-account-manager__name">
                {accountManagerName}
              </div>
              <div className="affiliate-account-manager__title">
                {accountManagerTitle}
              </div>
              <div className="affiliate__membership-sub-header affiliate__membership-sub-header--manager">
                <I18nText id="affiliate_three_sixty.affiliate.account_manager.title" />
              </div>
            </div>
          </div>
        ) : (
          <div className="affiliate-account-manager__unavailable-information">
            <Icon
              size="large"
              color="tertiary"
              name="person"
              showBGColor={true}
            />
            <I18nText id="affiliate_three_sixty.affiliate.account_manager.unavailable_information_text" />
          </div>
        )}

        <div className="affiliate-account-manager__modal">
          {hasAccountManager && (
            <IconButton
              version="v2"
              color="tertiary"
              icon={<Icon name="delete" size="normal" />}
              standalone={true}
              size="normal"
              label={
                <I18nText id="affiliate_three_sixty.affiliate.button_label.remove" />
              }
              onClick={() => onSelectedAccountManagerIdRemove(null)}
            />
          )}

          <div>
            {hasAccountManager ? (
              <IconButton
                version="v2"
                color="tertiary"
                icon={<Icon name="edit" size="normal" />}
                standalone={true}
                size="normal"
                onClick={onModalOpen}
                label={
                  <I18nText id="affiliate_three_sixty.affiliate.account_manager.edit" />
                }
              />
            ) : (
              <Button
                label={
                  <I18nText id="affiliate_three_sixty.affiliate.button_label.assign" />
                }
                version="v2"
                size="small"
                onClick={onModalOpen}
              />
            )}
          </div>

          <Modal
            className="modal__wrapper modal__wrapper-small"
            isVisible={modalOpen}
            onOutsideClick={onModalClose}
          >
            <div className="modal__header">
              <Modal.Title>
                {hasAccountManager ? (
                  <I18nText id="affiliate_three_sixty.affiliate.account_manager.header.change" />
                ) : (
                  <I18nText id="affiliate_three_sixty.affiliate.account_manager.header.assign" />
                )}
              </Modal.Title>
              <div
                onClick={onModalClose}
                onKeyDown={onModalClose}
                role="presentation"
              >
                <Icon name="close" size="normal" />
              </div>
            </div>

            <div className="modal__wrapper-overflow">
              <Modal.Content>
                <div className="affiliate-account-manager__modal-sub-header">
                  <I18nText id="affiliate_three_sixty.affiliate.account_manager.subHeader" />
                </div>
                <div className="col-12 col-bleed">
                  <SelectBox
                    width="full"
                    options={accountManagerOptions}
                    value={selectBoxObjectMapper(
                      accountManagerOptions,
                      selectedAccountManagerId,
                    )}
                    onChange={option =>
                      setSelectedAccountManagerId(option ? option.value : null)
                    }
                  />
                </div>
              </Modal.Content>
            </div>

            <Modal.Footer>
              <div className="col-12 col-bleed affiliate-account-manager__action">
                <Button
                  label={
                    !hasAccountManager ? (
                      <I18nText id="affiliate_three_sixty.affiliate.button_label.assign" />
                    ) : (
                      <I18nText id="affiliate_three_sixty.affiliate.button_label.save" />
                    )
                  }
                  version="v2"
                  size="small"
                  type="primary"
                  onClick={() =>
                    onSelectedAccountManagerIdAssign(selectedAccountManagerId)
                  }
                  disabled={!selectedAccountManagerId}
                />

                <Button
                  label={
                    <I18nText id="affiliate_three_sixty.affiliate.button_label.cancel" />
                  }
                  version="v2"
                  size="small"
                  onClick={onModalClose}
                />
              </div>
            </Modal.Footer>
          </Modal>
        </div>
      </div>
    </div>
  );
};

AffiliateAccountManager.defaultProps = {
  accountManagerId: null,
  accountManagerName: '',
  accountManagerAvatar: '',
  accountManagerTitle: '',
};

AffiliateAccountManager.propTypes = {
  hasAccountManager: PropTypes.bool.isRequired,
  accountManagerId: PropTypes.number,
  accountManagerName: PropTypes.string,
  accountManagerAvatar: PropTypes.string,
  accountManagerTitle: PropTypes.string,
  accountManagerOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      hintText: PropTypes.string,
    }),
  ).isRequired,
  updateAffiliate: PropTypes.func.isRequired,
};

export default AffiliateAccountManager;
